/**
 * Deduplicate identical citation rows within a document.
 *
 * The backend can cite several chunks of the same document that turn out to be
 * identical in what they show the user (same excerpt, same — or absent —
 * locator), e.g. for an image whose chunks overlap. Those render as repeated
 * reference rows (1.1, 1.2 showing the same text). This merges rows that are
 * IDENTICAL by content into a single reference, while keeping genuinely
 * distinct rows.
 *
 * Example: 1.1 and 1.2 identical, 1.3 different ->
 *   text [[1.1]],[[1.2]] -> [[1.1]];  [[1.3]] stays;  sources keep 1.1 and 1.3.
 * A later renumberCitations pass then makes the kept ids contiguous (1.1, 1.2).
 *
 * Rewrites BOTH the answer-text markers and the source list so chips and rows
 * stay in sync. Rows with a usable locator are de-duplicated too, but only when
 * truly identical (same locator + excerpt) — distinct pages/sections never
 * merge because their locator differs.
 */

import { ChatSource } from '../../api/chatTypes';
import { formatSourceLocator } from './sourceLocator';

export interface CollapseResult {
  content: string;
  sources: ChatSource[];
}

/** A stable signature of what a row actually shows the user. */
function rowSignature(source: ChatSource): string {
  const locator = source.locator ?? source.locators?.[0] ?? null;
  const locLabel = locator ? formatSourceLocator(locator) ?? '' : '';
  const excerpt = (source.excerpt ?? '').trim();
  const title = (source.title ?? '').trim();
  // Identical-content key: same document (prefix), same locator label, same
  // cited excerpt. Title is included so cross-document collisions cannot merge.
  return `${title} ${locLabel} ${excerpt}`;
}

export function collapseNoLocatorCitations(
  content: string,
  sources: ChatSource[]
): CollapseResult {
  if (sources.length < 2) {
    return { content, sources };
  }

  // old citation id -> kept citation id (for rewriting the answer text).
  const remap = new Map<string, string>();
  // signature, scoped per document prefix -> the kept source's id.
  const keptBySig = new Map<string, string>();
  const newSources: ChatSource[] = [];

  for (const s of sources) {
    if (s.citation_id === null || s.citation_id === undefined) {
      newSources.push(s);
      continue;
    }
    const id = String(s.citation_id);
    const docPrefix = id.split('.')[0];
    const sig = `${docPrefix}${rowSignature(s)}`;

    const keptId = keptBySig.get(sig);
    if (keptId !== undefined) {
      // Duplicate of an already-kept row: drop it, point its chips at the kept id.
      remap.set(id, keptId);
    } else {
      keptBySig.set(sig, id);
      newSources.push(s);
    }
  }

  if (remap.size === 0) {
    return { content, sources };
  }

  // Rewrite [[dup]] -> [[kept]] in the answer text (unknown ids untouched).
  const newContent = content.replace(/\[\[(\d+(?:\.\d+)?)\]\]/g, (full, id: string) => {
    const mapped = remap.get(id);
    return mapped ? `[[${mapped}]]` : full;
  });

  return { content: newContent, sources: newSources };
}
